/**
 * Settings > Strategy "Volume markers" tab: the whole rel-vol marker family
 * in one place, all on one baseline rule (utils/relVolBaseline.ts):
 *   🐋 Vest spikes (candles + volume pane): N, threshold, baseline
 *   🦈 CME spikes (candles + volume pane "2.4x"): N, threshold, baseline, multiples
 *   🐋🦈 both agree
 *   🐱 Vest bar above the CME bar in the volume pane (VestCatSettingsSection)
 * The 🪝 wick grade's spike factor reuses the 🐋/🦈 hits, so it follows these
 * rules (its own tab). Everything writes straight to the persisted stores
 * (levelsStore toggles, vestSpikeSettingsStore, volumePaneSettingsStore), the
 * same ones the Levels menu rows use, so it applies at once and both stay in
 * sync. Numbers commit on blur or Enter (clamped).
 */
import React, { useEffect, useState } from 'react';
import { useLevelsStore, type LevelType } from '../store/levelsStore';
import { useVestSpikeSettingsStore } from '../store/vestSpikeSettingsStore';
import { useVolumePaneSettingsStore } from '../store/volumePaneSettingsStore';
import { SPIKE_LOOKBACK_RANGE, SPIKE_THRESHOLD_RANGE } from '../utils/vestSpikes';
import { clampBaseline, TOD_MIN_SESSIONS, TOD_SESSIONS, type RelVolBaseline } from '../utils/relVolBaseline';
import { VestCatSettingsSection } from './VestCatSettings';
import { VEST_SPIKE_RULE_HINT } from './VestSpikeSettingsRow';
import BaselineHistoryHint from './BaselineHistoryHint';

const BASELINE_HINT = `auto = same Chicago time of day over the last ${TOD_SESSIONS} sessions the venue was open at that slot when ${TOD_MIN_SESSIONS}+ exist, else the previous N bars (the RTH open is compared with earlier opens); time of day = that only (needs ${TOD_MIN_SESSIONS}+ too, else no marker); trailing = previous N bars only. Hover a marker to see which basis it used.`;

/** Number input with a local draft that commits on blur / Enter. */
const DraftNumber: React.FC<{
  value: number;
  onCommit: (v: number) => void;
  read: () => number;
  min: number;
  max: number;
  step: number;
  aria: string;
  testId: string;
  disabled?: boolean;
}> = ({ value, onCommit, read, min, max, step, aria, testId, disabled }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const commit = () => {
    if (draft.trim() !== '' && Number.isFinite(Number(draft))) onCommit(Number(draft));
    setDraft(String(read()));
  };
  return (
    <input
      type="number"
      className="gs-input"
      min={min}
      max={max}
      step={step}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && commit()}
      aria-label={aria}
      data-testid={testId}
      disabled={disabled}
    />
  );
};

const BaselineSelect: React.FC<{ value: RelVolBaseline; onChange: (b: RelVolBaseline) => void; aria: string; testId: string; disabled?: boolean }> = ({
  value,
  onChange,
  aria,
  testId,
  disabled,
}) => (
  <select className="gs-input" value={value} onChange={(e) => onChange(clampBaseline(e.target.value))} aria-label={aria} data-testid={testId} disabled={disabled}>
    <option value="auto">auto</option>
    <option value="tod">time of day</option>
    <option value="trailing">trailing</option>
  </select>
);

const Toggle: React.FC<{ level: LevelType; label: string; hint: string; testId: string }> = ({ level, label, hint, testId }) => {
  const on = useLevelsStore((s) => !!s.visibility[level]);
  const setLevelVisible = useLevelsStore((s) => s.setLevelVisible);
  return (
    <div className="gs-row">
      <div className="gs-label">
        <span className="gs-label-main">{label}</span>
        <span className="gs-label-hint">{hint}</span>
      </div>
      <input type="checkbox" className="gs-checkbox" checked={on} onChange={(e) => setLevelVisible(level, e.target.checked)} aria-label={label} data-testid={testId} />
    </div>
  );
};

const Row: React.FC<{ label: string; hint: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div className="gs-row">
    <div className="gs-label">
      <span className="gs-label-main">{label}</span>
      <span className="gs-label-hint">{hint}</span>
    </div>
    {children}
  </div>
);

const VestSection: React.FC = () => {
  const { lookback, threshold, baseline, setLookback, setThreshold, setBaseline } = useVestSpikeSettingsStore();
  const get = useVestSpikeSettingsStore.getState;
  return (
    <div className="gs-section" data-testid="vm-vest-section">
      <div className="gs-section-header">
        <span className="gs-section-icon">🐋</span>
        <span className="gs-section-title">Vest volume spikes</span>
      </div>
      <Toggle level="vestSpikes" label="Show 🐋 on candles" hint="MNQ/NQ charts only. Above on Vest net buying, below on net selling" testId="vm-vest-candles" />
      <Toggle level="vestVolSpikes" label="Show 🐋 in the volume pane" hint="Over the thin Vest bar, same rule" testId="vm-vest-pane" />
      <Row label="Threshold (x baseline)" hint={`${VEST_SPIKE_RULE_HINT} Range ${SPIKE_THRESHOLD_RANGE.min}-${SPIKE_THRESHOLD_RANGE.max}.`}>
        <DraftNumber value={threshold} onCommit={setThreshold} read={() => get().threshold} min={SPIKE_THRESHOLD_RANGE.min} max={SPIKE_THRESHOLD_RANGE.max} step={0.05} aria="Vest spike threshold" testId="vm-vest-threshold" />
      </Row>
      <Row label="Baseline" hint={BASELINE_HINT}>
        <BaselineSelect value={baseline} onChange={setBaseline} aria="Vest spike baseline" testId="vm-vest-baseline" />
      </Row>
      <BaselineHistoryHint venue="vest" baseline={baseline} style={{ padding: '0 12px 4px' }} />
      <Row label="Trailing bars N" hint={`Used by trailing, and by auto until ${TOD_MIN_SESSIONS} earlier sessions exist at that time of day. Range ${SPIKE_LOOKBACK_RANGE.min}-${SPIKE_LOOKBACK_RANGE.max}.`}>
        <DraftNumber value={lookback} onCommit={setLookback} read={() => get().lookback} min={SPIKE_LOOKBACK_RANGE.min} max={SPIKE_LOOKBACK_RANGE.max} step={1} aria="Vest spike lookback bars" testId="vm-vest-lookback" disabled={baseline === 'tod'} />
      </Row>
    </div>
  );
};

const CmeSection: React.FC = () => {
  const cme = useVolumePaneSettingsStore((s) => s.cme);
  const setCme = useVolumePaneSettingsStore((s) => s.setCme);
  const showMultiples = useVolumePaneSettingsStore((s) => s.showSpikeMultiples);
  const setShowMultiples = useVolumePaneSettingsStore((s) => s.setShowSpikeMultiples);
  const get = () => useVolumePaneSettingsStore.getState().cme;
  return (
    <div className="gs-section" data-testid="vm-cme-section">
      <div className="gs-section-header">
        <span className="gs-section-icon">🦈</span>
        <span className="gs-section-title">CME volume spikes</span>
      </div>
      <Toggle level="cmeSpikes" label="Show 🦈 on candles" hint="On the side of the CME bar delta" testId="vm-cme-candles" />
      <Toggle level="cmeVolSpikes" label="Highlight spikes in the volume pane" hint="Full-strength bar plus its multiple (2.4x)" testId="vm-cme-pane" />
      <Row label="Show spike multiples" hint="Off: no 2.4x text; the brighter bar and the 🐋 stay">
        <input type="checkbox" className="gs-checkbox" checked={showMultiples} onChange={(e) => setShowMultiples(e.target.checked)} aria-label="Show spike multiples" data-testid="vm-cme-multiples" />
      </Row>
      <Row label="Threshold (x baseline)" hint="CME rel vol = bar volume / baseline per 2m base bar; closed bars only, 15 min after a session (re)open skipped. Range 1.1-20.">
        <DraftNumber value={cme.threshold} onCommit={(v) => setCme({ threshold: v })} read={() => get().threshold} min={1.1} max={20} step={0.1} aria="CME spike threshold" testId="vm-cme-threshold" />
      </Row>
      <Row label="Baseline" hint={BASELINE_HINT}>
        <BaselineSelect value={cme.baseline} onChange={(b) => setCme({ baseline: b })} aria="CME spike baseline" testId="vm-cme-baseline" />
      </Row>
      <BaselineHistoryHint venue="cme" baseline={cme.baseline} style={{ padding: '0 12px 4px' }} />
      <Row label="Trailing bars N" hint={`Used by trailing, and by auto until ${TOD_MIN_SESSIONS} earlier sessions exist at that time of day. Range 5-500.`}>
        <DraftNumber value={cme.lookback} onCommit={(v) => setCme({ lookback: v })} read={() => get().lookback} min={5} max={500} step={1} aria="CME spike lookback bars" testId="vm-cme-lookback" disabled={cme.baseline === 'tod'} />
      </Row>
    </div>
  );
};

export const VolumeMarkersSettingsPanel: React.FC = () => (
  <div className="gs-tab-content" data-testid="volume-markers-panel">
    <VestSection />
    <CmeSection />
    <div className="gs-section" data-testid="vm-both-section">
      <div className="gs-section-header">
        <span className="gs-section-icon">🐋🦈</span>
        <span className="gs-section-title">Both agree</span>
      </div>
      <Toggle level="bothSpikes" label="Show 🐋🦈 on candles" hint="Both venues spiked on the same closed bar (the 🐋 and 🦈 rules above) and their deltas agree" testId="vm-both" />
    </div>
    <VestCatSettingsSection />
    <div className="gs-row">
      <div className="gs-label">
        <span className="gs-label-hint">
          The 🪝 wick grade (its own tab) counts a 🐋 or 🦈 on the same bar with exactly these rules. Also in the Levels menu (Volume). Changes apply immediately and are saved in this browser.
        </span>
      </div>
    </div>
  </div>
);
